import Drawer from 'components/Drawer';
import { BodySemi, CaptionRegular } from 'components/Text';
import { GOOGLE_METADATA_BY_KEY, POPULARITY_SORT_OPTIONS } from 'utils/metrics';
import { TableStack } from '../RelatedHashtagsTable/styledComponents';
import {
    filterHealthStats,
    filterNames,
    filterParentName,
} from 'utils/filters';
import { DataTable } from '../DataTable';
import DownloadButton from '../../Button/DownloadButton';
import { OverflowEllipsis } from 'utils/fragments';
import PopularityIndexRelatedEntitiesDrawer from './PopularityIndexRelatedEntitiesDrawer';
import { RelatedHashtagsTableHeadColFilter } from '../DataTable/styledComponents';
import SectionTableFilterChips from '../../SectionTableFilterChips';
import Stack from '../../Stack';
import TableHeading from '../../TableHeading';
import { createDefaultColumns } from '../DataTable/utils';
import { downloadItemsAsCSV } from 'utils/formatCSV';
import sortDataByKey from 'utils/sortDataByKey';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import usePopularityRelatedEntities from 'utils/hooks/usePopularityRelatedEntities';
import useRelatedEntitiesTableProps from './useRelatedEntitiesTableProps.hook';

const ParentEntity = styled(CaptionRegular)`
    max-width: 100%;
    ${OverflowEllipsis};
`;

export const relatedHashtagCell = ({ value: hashtag, row, css }) => {
    return (
        <Stack css={css} $space="-.25rem" $direction="column">
            <ParentEntity as="div">{row?.relatedName}</ParentEntity>
            <BodySemi as="div">{hashtag}</BodySemi>
        </Stack>
    );
};

const brandsHandleFilter = {
    healthStats: filterHealthStats,
    name: filterNames,
};

const categoriesHandleFilter = {
    ...brandsHandleFilter,
    popIndexParentName: filterParentName,
};

function PopularityIndexRelatedEntitiesSectionTable({
    parentEntity,
    parentEntityId,
    parentEntityType,
    affinityGroups,
    paginateBy,
    brands,
    categories,
    countries,
    week,
    year,
    initialSortOption = POPULARITY_SORT_OPTIONS.popularity_index_avg_12_month,
    initialFilterOption = {},
    icon,
    title,
    subtitle,
    count,
    hideCount = false,
    tools,
    tooltipKey = 'popularityIndexRelatedEntities',
    tooltipExample,
    filename = 'trend table',
    name,
    showAddToWatchlist = true,
    filterParamSeparator,
    filterParamPrefix,
    sortParamKey,
    ...restProps
}) {
    const { query: countQuery } = usePopularityRelatedEntities({
        categories: categories || null,
        brands: brands || null,
        countries: countries || null,
        paginateBy,
        per_page: 1,
        enabled: !!((categories || brands) && countries),
    });
    const totalRecords = countQuery.isPreviousData
        ? null
        : (countQuery.data?.pages?.[0]?.total_records ?? null);
    const getColumnsConfig =
        paginateBy === 'brands'
            ? relatedBrandsGetColumnsConfig
            : defaultGetColumnsConfig;
    const handleFilter =
        paginateBy === 'brands' ? brandsHandleFilter : categoriesHandleFilter;
    const {
        columnsMenu,
        dataReady,
        isLoading,
        noDataMessage,
        rows,
        sortedData,
        filters,
        filtersKey,
        setFilter,
        setFilters,
        sortOption,
        setSortOption,
        sortOptionKey,
        maxSortMetric,
        visibleColumnsConfig,
        handleSearchInputChange,
        queryData,
        fetchNextPage,
        isFetchingNextPage,
        isFetching,
        getQueryData,
    } = useRelatedEntitiesTableProps({
        affinityGroups,
        brands,
        categories,
        countries,
        initialSortOption,
        initialFilterOption,
        getColumnsConfig,
        handleFilter,
        paginateBy,
        name,
        filterParamSeparator,
        filterParamPrefix,
        sortParamKey,
    });

    return (
        <>
            <TableHeading
                icon={icon}
                title={title}
                subtitle={subtitle}
                {...(!hideCount && { count: totalRecords })}
                tooltipKey={tooltipKey}
                tooltipExample={tooltipExample}
                tools={
                    <>
                        {tools}
                        <DownloadButton
                            disabled={!dataReady}
                            isLoading={isLoading}
                            onClick={() => {
                                toast.promise(
                                    (async () => {
                                        try {
                                            const columnsConfigCopy =
                                                visibleColumnsConfig.map(
                                                    (column) => ({ ...column }),
                                                );
                                            const allData =
                                                await getQueryData();
                                            downloadItemsAsCSV(
                                                filename,
                                                allData,
                                                columnsConfigCopy,
                                            );
                                        } catch (error) {
                                            console.error(
                                                'Error downloading CSV:',
                                                error,
                                            );
                                            throw error;
                                        }
                                    })(),
                                    {
                                        loading: 'Preparing download...',
                                        success: 'Download complete!',
                                        error: 'Error downloading CSV',
                                    },
                                );
                            }}
                            mixpanelMeta={{ contentKey: 'HashtagDataTable' }}
                        />
                    </>
                }
            />
            <TableStack>
                <div
                    css={`
                        display: flex;
                        flex-wrap: nowrap;
                        gap: 0.75rem;
                        align-items: flex-start;
                    `}
                >
                    <div
                        css={`
                            flex-grow: 1;
                        `}
                    >
                        <SectionTableFilterChips
                            handleFilter={handleFilter}
                            filters={filters}
                            setFilter={setFilter}
                            setFilters={setFilters}
                            queryData={queryData}
                        />
                    </div>
                    {columnsMenu}
                </div>
                <DataTable
                    isLoading={isLoading}
                    isFetching={isFetching}
                    fetchNextPage={fetchNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    data={sortedData}
                    dataKey={`d-*-s-${sortOptionKey}-f-${filtersKey}`}
                    noDataMessage={noDataMessage}
                    sortOption={sortOption}
                    setSortOption={setSortOption}
                    maxSortMetric={maxSortMetric}
                    name={name}
                    onSearchInputChange={handleSearchInputChange}
                    limitSearchWords={3}
                    DrawerComponent={Drawer}
                    TableHeadColFilter={RelatedHashtagsTableHeadColFilter}
                    columns={visibleColumnsConfig}
                    week={week}
                    year={year}
                    rowHeight={60}
                    renderDrawerContent={(selectedId) => (
                        <PopularityIndexRelatedEntitiesDrawer
                            selectedId={selectedId}
                            selectedRow={rows.find(
                                (row) => row.id === selectedId,
                            )}
                            paginateBy={paginateBy}
                            countryId={countries ? countries[0] : null}
                            parentEntity={parentEntity}
                            parentEntityId={parentEntityId}
                            parentEntityType={parentEntityType}
                            week={week}
                            year={year}
                            showAddToWatchlist={showAddToWatchlist}
                        />
                    )}
                    {...restProps}
                />
            </TableStack>
        </>
    );
}

const defaultGetColumnsConfig = (visibleOrderedMetrics) => {
    const columnsConfig = createDefaultColumns(
        ['name', 'parentName', ...visibleOrderedMetrics],
        { showHealthStatLabels: true },
    );
    columnsConfig[0].formatCell = relatedHashtagCell;
    return columnsConfig;
};

const relatedBrandsGetColumnsConfig = (visibleOrderedMetrics) => {
    const columnsConfig = createDefaultColumns(
        [
            'name',
            GOOGLE_METADATA_BY_KEY.price_segmentation,
            ...visibleOrderedMetrics,
        ],
        { showHealthStatLabels: true },
    );
    columnsConfig[0].formatCell = relatedHashtagCell;
    return columnsConfig;
};

export default PopularityIndexRelatedEntitiesSectionTable;
